<template>
  <div>
    <!-- Full-screen loading overlay -->
    <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
      </div>
    </v-overlay>

    <v-card>
      <v-card-title class="text-h6 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-account-multiple</v-icon>
          Danh sách người dùng
        </div>
        <v-text-field
          v-model="search"
          label="Tìm kiếm người dùng"
          @input="onSearchInput"
          clearable
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          class="search-input"
          style="max-width: 320px;"
        />
      </v-card-title>
      <v-card-text>
        <!-- Desktop Table View -->
        <div v-if="!$vuetify.display.mobile" class="desktop-view">
          <v-table class="elevation-1">
            <thead>
              <tr>
                <th>Email</th>
                <th>Số điện thoại</th>
                <th>Số dư</th>
                <th>Thông tin ngân hàng</th>
                <th>Tên</th>
                <th>Ngày tạo</th>
                <th class="text-center">Trạng thái</th>
                <th class="text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in allUsers" :key="user.id">
                <td>{{ user.email }}</td>
                <td>{{ user.phone || 'N/A' }}</td>
                <td>{{ formatCurrency(user.balance) }}</td>
                <td>
                  <div v-if="user.bank_name || user.bank_account || user.bank_holder" class="bank-info">
                    <div v-if="user.bank_name" class="bank-detail">
                      <v-icon size="14" color="primary" class="mr-1">mdi-bank</v-icon>
                      <span class="text-caption">{{ user.bank_name }}</span>
                    </div>
                    <div v-if="user.bank_account" class="bank-detail">
                      <v-icon size="14" color="success" class="mr-1">mdi-credit-card</v-icon>
                      <span class="text-caption">{{ user.bank_account }}</span>
                    </div>
                    <div v-if="user.bank_holder" class="bank-detail">
                      <v-icon size="14" color="info" class="mr-1">mdi-account</v-icon>
                      <span class="text-caption">{{ user.bank_holder }}</span>
                    </div>
                  </div>
                  <div v-else class="text-caption text-grey">
                    <v-icon size="14" color="grey" class="mr-1">mdi-information-outline</v-icon>
                    N/A
                  </div>
                </td>
                <td>{{ user.name || 'N/A' }}</td>
                <td>{{ formatDate(user.created_at) }}</td>
                <td class="text-center">
                  <v-chip :color="user.status === 1 ? 'success' : 'error'" size="small">
                    {{ user.status === 1 ? 'Hoạt động' : 'Bị khóa' }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <v-btn 
                    icon 
                    size="small" 
                    :color="user.status === 1 ? 'error' : 'success'" 
                    @click="toggleUserStatus(user)"
                    :title="user.status === 1 ? 'Khóa tài khoản' : 'Mở khóa tài khoản'"
                  >
                    <v-icon>{{ user.status === 1 ? 'mdi-lock' : 'mdi-lock-open' }}</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-pagination
            v-model="page"
            :length="totalPages"
            @update:modelValue="onPageChange"
            class="custom-pagination"
          />
        </div>

        <!-- Mobile Card View -->
        <div v-else class="mobile-view">
          <div class="mobile-users-container" ref="usersContainer">
            <v-card 
              v-for="user in allUsers" 
              :key="user.id" 
              class="mb-3 user-card"
              elevation="2">
              <v-card-text>
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="user-name font-weight-bold">{{ user.name || user.email }}</div>
                  <v-chip :color="user.status === 1 ? 'success' : 'error'" size="small">
                    {{ user.status === 1 ? 'Hoạt động' : 'Bị khóa' }}
                  </v-chip>
                </div>
                
                <div class="user-info">
                  <div class="info-row">
                    <v-icon size="16" color="primary" class="mr-2">mdi-email</v-icon>
                    <strong>Email:</strong>
                    <span class="ml-2">{{ user.email }}</span>
                  </div>
                  
                  <div class="info-row" v-if="user.phone">
                    <v-icon size="16" color="success" class="mr-2">mdi-phone</v-icon>
                    <strong>SĐT:</strong>
                    <span class="ml-2">{{ user.phone }}</span>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="warning" class="mr-2">mdi-wallet</v-icon>
                    <strong>Số dư:</strong>
                    <span class="ml-2 font-weight-bold text-warning">{{ formatCurrency(user.balance) }}</span>
                  </div>
                  
                  <div class="info-row" v-if="user.bank_name || user.bank_account || user.bank_holder">
                    <v-icon size="16" color="info" class="mr-2">mdi-bank</v-icon>
                    <strong>Ngân hàng:</strong>
                    <div class="ml-2">
                      <div v-if="user.bank_name" class="bank-detail">
                        <v-icon size="12" color="primary" class="mr-1">mdi-bank</v-icon>
                        <span class="text-caption">{{ user.bank_name }}</span>
                      </div>
                      <div v-if="user.bank_account" class="bank-detail">
                        <v-icon size="12" color="success" class="mr-1">mdi-credit-card</v-icon>
                        <span class="text-caption">{{ user.bank_account }}</span>
                      </div>
                      <div v-if="user.bank_holder" class="bank-detail">
                        <v-icon size="12" color="info" class="mr-1">mdi-account</v-icon>
                        <span class="text-caption">{{ user.bank_holder }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="info-row">
                    <v-icon size="16" color="grey" class="mr-2">mdi-calendar</v-icon>
                    <strong>Ngày tạo:</strong>
                    <span class="ml-2">{{ formatDate(user.created_at) }}</span>
                  </div>
                </div>
                
                <div class="d-flex justify-center mt-3">
                  <v-btn 
                    :color="user.status === 1 ? 'error' : 'success'" 
                    size="small" 
                    @click="toggleUserStatus(user)"
                    block>
                    <v-icon class="mr-2">{{ user.status === 1 ? 'mdi-lock' : 'mdi-lock-open' }}</v-icon>
                    {{ user.status === 1 ? 'Khóa tài khoản' : 'Mở khóa tài khoản' }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </div>
          
          <!-- Loading indicator for infinite scroll -->
          <div v-if="loadingMore" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2">Đang tải thêm...</div>
          </div>
          
          <!-- End of list indicator -->
          <div v-if="hasReachedEnd && allUsers.length > 0" class="text-center py-4 text-grey">
            <v-icon>mdi-check-circle</v-icon>
            <div>Đã hiển thị tất cả người dùng</div>
          </div>
        </div>
      </v-card-text>
    </v-card>
    
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/http';

export default {
  name: 'User',
  data() {
    return {
      users: [],
      allUsers: [], // For infinite scroll
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
      search: '',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      pageLoading: false,
      loadingMore: false, // For infinite scroll loading
      hasReachedEnd: false, // Track if we've loaded all data
      currentMobilePage: 1 // Separate page tracking for mobile
    }
  },
  methods: {
    async fetchUsers(page = this.page, limit = this.limit, search = this.search, showPageLoading = false, append = false) {
      if (showPageLoading) {
        this.pageLoading = true;
      }
      if (append) {
        this.loadingMore = true;
      }
      
      try {
        const res = await axios.get('/users', {
          params: { page, limit, search }
        });
        
        if (append) {
          // For mobile infinite scroll
          this.allUsers = [...this.allUsers, ...res.data.data];
          this.hasReachedEnd = res.data.data.length < limit || page >= res.data.pagination.pages;
        } else {
          // For desktop pagination or initial load
          this.users = res.data.data;
          this.allUsers = res.data.data;
        }
        
        this.total = res.data.total;
        this.totalPages = res.data.pagination.pages;
      } catch (e) {
        this.snackbarText = 'Lấy danh sách người dùng thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        if (showPageLoading) {
          this.pageLoading = false;
        }
        if (append) {
          this.loadingMore = false;
        }
      }
    },

    async loadMoreUsers() {
      if (this.loadingMore || this.hasReachedEnd) return;
      
      this.currentMobilePage++;
      await this.fetchUsers(
        this.currentMobilePage, 
        this.limit, 
        this.search, 
        false, 
        true // append = true
      );
    },

    resetMobileData() {
      this.allUsers = [];
      this.currentMobilePage = 1;
      this.hasReachedEnd = false;
    },
    
    onSearchInput() {
      clearTimeout(this._searchTimeout);
      this._searchTimeout = setTimeout(() => {
        this.page = 1;
        this.resetMobileData();
        this.fetchUsers(1, this.limit, this.search);
      }, 400);
    },
    
    onPageChange(newPage) {
      this.page = newPage;
      this.fetchUsers(newPage, this.limit, this.search);
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleString('vi-VN');
    },
    
    formatCurrency(amount) {
      if (!amount && amount !== 0) return 'N/A';
      return amount.toLocaleString('vi-VN') + '₫';
    },
    
    async toggleUserStatus(user) {
      try {
        const newStatus = user.status === 1 ? 0 : 1;
        await axios.put(`/users/${user.id}/status`, { status: newStatus });
        user.status = newStatus;
        this.snackbarText = `${newStatus === 1 ? 'Mở khóa' : 'Khóa'} tài khoản thành công!`;
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (e) {
        this.snackbarText = 'Cập nhật trạng thái tài khoản thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },

    setupInfiniteScroll() {
      if (!('IntersectionObserver' in window)) return;
      
      this.$nextTick(() => {
        if (this.$refs.usersContainer) {
          this.observer = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting && this.$vuetify.display.mobile) {
              this.loadMoreUsers();
            }
          }, {
            root: null,
            rootMargin: '200px',
            threshold: 0.1
          });
          
          // Create a sentinel element at the bottom
          const sentinel = document.createElement('div');
          sentinel.style.height = '1px';
          sentinel.style.background = 'transparent';
          this.$refs.usersContainer?.appendChild(sentinel);
          this.observer.observe(sentinel);
        }
      });
    }
  },
  
  mounted() {
    this.fetchUsers(this.page, this.limit, this.search, true); // Show page loading on initial load
    this.setupInfiniteScroll();
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
</script>

<style scoped>
.search-input {
  max-width: 320px;
  margin-bottom: 0;
}

.custom-pagination {
  margin-top: 20px;
}

.bank-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 180px;
}

.bank-detail {
  display: flex;
  align-items: center;
  padding: 1px 0;
}

.bank-detail .text-caption {
  font-size: 0.75rem !important;
  line-height: 1.2;
}

/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-users-container {
  padding: 8px 0;
}

.user-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.user-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 70px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

.user-name {
  word-break: break-word;
  line-height: 1.3;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .bank-info {
    min-width: 140px;
  }
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .search-input {
    max-width: 100% !important;
    margin-bottom: 16px;
  }
  
  .v-card-title {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px;
  }
  
  .mobile-users-container {
    padding: 4px 0;
  }
  
  .user-card {
    margin-bottom: 12px !important;
  }
  
  .info-row {
    font-size: 13px;
  }
  
  .info-row strong {
    min-width: 65px;
    font-size: 13px;
  }
  
  .user-name {
    font-size: 15px;
  }
}
</style>